import { Box, Tabs, TabsProps, Typography } from '@mui/material';
import { SxProps, Theme } from '@mui/system';
import React, { FC, useEffect } from 'react';
import { StyledAppBar, StyledDivider, StyledHeaderBox, StyledTab } from './CfTabs.styles';

/**
 * the properties which relate to each item/tab of a CfTabs component
 */
export interface TabItem {
  label: string;
  children?: React.ReactNode;
  disabled?: boolean;
  dataTestId?: string;
}

/**
 * the properties which describe how the CfTabs component should present and behave
 */
export interface CfTabsProps extends TabsProps {
  tabs: TabItem[];
  onChange?: (event: React.SyntheticEvent, index: number) => void;
  smallDivider?: boolean;
  tabsetPrefix?: React.ReactNode;
  tabsetSuffix?: React.ReactNode;
  containerSx?: SxProps<Theme>;
  hideDivider?: boolean;
  uncontrolledInput?: boolean;
}

/**
 * Allows users to switch between labeled sections of content utilizing the AppBar and Tabs components
 * @param tabs mapped into a set of a Box components - only the selected tab is displayed
 * @param onChange executed when the Tabs component's onChange event is triggered
 * @param smallDivider determines the width of the AppBar component
 * @param tabsetPrefix rendered before the AppBar component
 * @param tabsetSuffix rendered after the AppBar component
 * @param containerSx passed to the (root) Box component's sx property
 * @param hideContainer passed to the Tabs component's hideContainer property
 * @param  uncontrolledInput determines whether the Tabs component's onChange event handler should immediately update the component's currentTab state object
 * @returns
 */
export const CfTabs: FC<CfTabsProps> = (props: CfTabsProps) => {
  const {
    tabs,
    onChange,
    smallDivider,
    tabsetPrefix,
    tabsetSuffix,
    containerSx: sx,
    hideDivider,
    uncontrolledInput,
    ...rest
  } = props;

  const [currentTab, setCurrentTab] = React.useState(props.value ?? 0);

  useEffect(() => {
    if (typeof props.value !== 'undefined') setCurrentTab(props.value);
  }, [props.value]);

  const handleChange = (event: React.SyntheticEvent, index: number) => {
    if (!uncontrolledInput) setCurrentTab(index);
    onChange?.(event, index);
  };

  return (
    <Box sx={sx}>
      <StyledHeaderBox pb={1}>
        {tabsetPrefix && tabsetPrefix}
        <StyledAppBar position='static' sx={{ width: smallDivider ? 'fit-content' : '100%' }}>
          <Tabs {...rest} value={currentTab} onChange={handleChange} aria-label='cf-tabs'>
            {tabs.map((item: TabItem, index: number) => {
              return (
                <StyledTab
                  key={index}
                  label={
                    <Typography variant={currentTab === index ? 'responsiveParagraphBold' : 'responsiveParagraph'}>
                      {item.label}
                    </Typography>
                  }
                  {...getTabControlProps(index)}
                  disableRipple
                  disabled={item.disabled}
                  data-testid={item.dataTestId}
                />
              );
            })}
          </Tabs>
          {!hideDivider && <StyledDivider />}
        </StyledAppBar>
        {tabsetSuffix && tabsetSuffix}
      </StyledHeaderBox>
      {tabs.map((item: TabItem, index: number) => {
        return (
          <Box
            key={index}
            role='tabpanel'
            hidden={index !== currentTab}
            {...getPanelControlProps(index)}
            sx={{ width: '100%' }}
          >
            {currentTab === index && item.children}
          </Box>
        );
      })}
    </Box>
  );
};

function getTabControlProps(index: number) {
  return {
    id: `cf-tab-${index}`,
    'aria-controls': `cf-tabpanel-${index}`,
    tabIndex: 0,
  };
}

function getPanelControlProps(index: number) {
  return {
    id: `cf-tabpanel-${index}`,
    'aria-labelledby': `cf-tab-${index}`,
  };
}
